import { assetUrl } from './assets';
export type GameMode = 'casual' | 'challenge';
export type CounterId = 0 | 1 | 2 | 3 | 4 | 5;
export interface ChallengeReward { id: number; stage: number; first: CounterId; second: CounterId }
export interface UnityEvent { type: 'ready' | 'score' | 'phase' | 'power' | 'loading' | 'error' | 'challenge' | 'reward-video-request'; phase: 'home' | 'loading' | 'playing' | 'paused' | 'over'; score: number; best: number; highest: number; theme: string; combo: number; power?: number; progress?: number; error?: string; mode?: GameMode; stage?: number; bestStage?: number; shots?: number; shotsTotal?: number; danger?: number; counters?: CounterId[]; pendingRewards?: number; reward?: ChallengeReward | null; counterSelecting?: boolean; counterCasting?: boolean; counterSlot?: number; rewardOpen?: boolean; videoToken?: string; videoKind?: number; videoReady?: boolean; videoShowing?: boolean; videoCompleted?: number }
export interface UnityPreferences { theme: string; language: string; sound: boolean; mode: GameMode; best: number; bestStage: number; reduceMotion: boolean }
interface Instance { SendMessage(object: string, method: string, value?: string): void; Quit(): Promise<void> }
interface ThemeBundle { id: string; file: string; hash: string; crc: number; bytes: number }
interface Manifest { loader: string; data: string; framework: string; code: string; version: string; runtimeVersion: string; bytes: Record<string, number>; themes: ThemeBundle[] }
export type LoadingStage = 'game' | 'theme';
declare global { interface Window { createUnityInstance?: (canvas: HTMLCanvasElement, configuration: object, progress: (value: number) => void) => Promise<Instance> } }

// The website never simulates a ball. Unity owns input, physics, meshes,
// particles, merge timing, score rules and the game scene's lifetime.
export class UnityGame {
  private instance?: Instance;
  private preferences?: UnityPreferences;
  private readyResolve?: () => void;
  private configuredResolve?: () => void;
  private configuredReject?: (error: Error) => void;
  private themeProgress?: (value: number) => void;
  private loaderUrl?: string;
  private viewportObserver: ResizeObserver;
  private version = 0;
  constructor(private canvas: HTMLCanvasElement, private receive: (message: UnityEvent) => void) {
    this.viewportObserver = new ResizeObserver(() => this.syncViewport());
    this.viewportObserver.observe(canvas);
    window.addEventListener('sunny-bounce-unity', event => {
      const message = (event as CustomEvent<UnityEvent>).detail;
      if (message.type === 'ready') {
        this.readyResolve?.();
      }
      if (message.type === 'loading') { this.themeProgress?.(message.progress ?? 0); return; }
      if (message.type === 'error') { this.configuredReject?.(new Error(message.error || 'Theme could not be loaded.')); return; }
      if (message.type === 'phase' && message.phase === 'playing') this.configuredResolve?.();
      this.receive(message);
    });
  }
  async load(preferences: UnityPreferences, progress: (value: number, stage: LoadingStage) => void) {
    const version = ++this.version; this.preferences = preferences;
    const ready = new Promise<void>(resolve => { this.readyResolve = resolve; });
    const response = await fetch(assetUrl('unity/manifest.json'), { cache: 'no-store' });
    if (!response.ok) throw new Error('Unity WebGL build is unavailable.');
    const manifest: Manifest = await response.json();
    const theme = manifest.themes.find(item => item.id === preferences.theme);
    if (!theme) throw new Error('The selected theme has no content bundle.');
    // Content-hashed filenames invalidate changed content without invalidating
    // every cached theme when only the player or another theme changes.
    const buildUrl = (relative: string) => new URL(assetUrl('unity/' + relative), document.baseURI).href;
    const commonBytes = Object.values(manifest.bytes).reduce((sum, bytes) => sum + bytes, 0);
    const commonShare = commonBytes / (commonBytes + theme.bytes);
    this.themeProgress = value => { if (version === this.version) progress(commonShare + value * (1 - commonShare), 'theme'); };
    const loaderUrl = buildUrl(manifest.loader);
    if (!window.createUnityInstance || this.loaderUrl !== loaderUrl) await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script'); script.src = loaderUrl;
      script.onload = () => resolve(); script.onerror = () => { script.remove(); reject(new Error('Unity loader did not load.')); }; document.head.append(script);
    });
    this.loaderUrl = loaderUrl;
    if (version !== this.version) return;
    const instance = await window.createUnityInstance!(this.canvas, {
      dataUrl: buildUrl(manifest.data), frameworkUrl: buildUrl(manifest.framework),
      codeUrl: buildUrl(manifest.code), streamingAssetsUrl: assetUrl('unity/StreamingAssets'),
      companyName: 'SunnyBounceWeb', productName: 'Sunny Bounce 2048 Web', productVersion: manifest.runtimeVersion,
      cacheControl: (url: string) => /\/ThemeBundles\/|\.(data|wasm)\.unityweb(?:\?|$)/.test(url) ? 'immutable' : 'must-revalidate',
      matchWebGLToCanvasSize: true, devicePixelRatio: Math.min(2, window.devicePixelRatio || 1),
    }, value => { if (version === this.version) progress(value * commonShare, 'game'); });
    if (version !== this.version) { await instance.Quit(); return; }
    this.instance = instance;
    this.syncViewport();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([ready, new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('Game scene did not become ready.')), 120000); })]);
      if (version !== this.version) return;
      clearTimeout(timer); this.themeProgress(0);
      const configured = new Promise<void>((resolve, reject) => { this.configuredResolve = resolve; this.configuredReject = reject; });
      this.send('Configure', JSON.stringify({ ...this.preferences, theme: theme.id,
        bundleUrl: buildUrl(theme.file), bundleHash: theme.hash, bundleCrc: theme.crc }));
      await Promise.race([configured, new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Theme loading timed out.')), 150000);
      })]);
      if (version === this.version) progress(1, 'theme');
    } finally { clearTimeout(timer); this.configuredResolve = undefined; this.configuredReject = undefined; this.themeProgress = undefined; }
  }
  get loaded() { return !!this.instance; }
  private syncViewport() {
    const bounds = this.canvas.getBoundingClientRect();
    if (bounds.width > 0 && bounds.height > 0) this.send('SetViewport', JSON.stringify({ width: bounds.width, height: bounds.height }));
  }
  send(method: string, value = '') { this.instance?.SendMessage('WebCasualBridge', method, value); }
  configure(preferences: UnityPreferences) { this.preferences = preferences; }
  async quit() {
    this.version++; const instance = this.instance; this.instance = undefined; this.preferences = undefined; this.readyResolve?.();
    this.configuredResolve?.(); this.themeProgress = undefined;
    if (instance) await instance.Quit();
  }
}
